import React, { createContext, useMemo, type PropsWithChildren } from 'react';
import { useLoomTouchpointVariantWithAdControls } from '@atlassian/jira-issue-view-common/src/component/loom-videos/use-loom-touchpoint-variant-with-ad-controls/index.tsx';
import { LOOM_QUICK_ADD_AD_CONTROL_DISMISS_KEY } from '@atlassian/jira-issue-view-common/src/component/loom-videos/constants.tsx';
import type { LoomTouchpointVariantType } from '@atlassian/jira-loom-utils/src/controllers/use-loom-touchpoint-variant/types.tsx';
import { useUserHasConfluenceAccess } from '@atlassian/jira-confluence-integration-controls/src/controllers/use-user-has-confluence-access/index.tsx';
import { JIRA_ISSUE_QUICK_ACTIONS } from '@atlassian/jira-issue-create-confluence-content/src/common/constants/embedded-confluence-source.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { ISSUE_TYPE, STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { IssueType } from '@atlassian/jira-issue-shared-types/src/common/types/issue-hierarchy-type.tsx';
import type { StatusDetails } from '@atlassian/jira-issue-shared-types/src/common/types/status-type.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsOrgAdmin } from '@atlassian/jira-business-goal-picker/src/services/is-org-admin/index.tsx';
import { tenantHasActiveProductDiscoveryLicenseSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/context-selector.tsx';
import { useSelector } from '@atlassian/jira-react-redux/src/index.tsx';

type CrossFlowPostOfficeContextType = {
	loomTouchpointVariant: LoomTouchpointVariantType | null;
	hasConfluenceAccess: boolean | null;
	issueType: string | null;
	issueStatusDetails: string | null;
	isAdmin: boolean | null;
	isOrgAdmin: boolean | null;
	hasActiveJpdLicense: boolean | null;
};

export const CrossFlowPostOfficeContext = createContext<CrossFlowPostOfficeContextType>({
	loomTouchpointVariant: null,
	hasConfluenceAccess: null,
	issueType: null,
	issueStatusDetails: null,
	isAdmin: null,
	isOrgAdmin: null,
	hasActiveJpdLicense: null,
});

export const CrossFlowPostOfficeProvider = ({ children }: PropsWithChildren<unknown>) => {
	const { loomTouchpointVariant } = useLoomTouchpointVariantWithAdControls(
		LOOM_QUICK_ADD_AD_CONTROL_DISMISS_KEY,
	);
	const hasConfluenceAccess = useUserHasConfluenceAccess(JIRA_ISSUE_QUICK_ACTIONS).hasAccess;
	const issueKey = useIssueKey();
	const [issueType]: [IssueType, unknown] = useFieldValue({
		issueKey,
		fieldKey: ISSUE_TYPE,
	});
	const [issueStatusDetails]: [StatusDetails, unknown] = useFieldValue({
		issueKey,
		fieldKey: STATUS_TYPE,
	});
	const isAdmin = useIsAdmin();
	const { isOrgAdmin } = useIsOrgAdmin();
	const hasActiveJpdLicense: boolean = useSelector(tenantHasActiveProductDiscoveryLicenseSelector);

	const context = useMemo(() => {
		return {
			loomTouchpointVariant,
			hasConfluenceAccess,
			issueType: JSON.stringify(issueType), // post office context expects primitive data types
			issueStatusDetails: JSON.stringify(issueStatusDetails), // post office context expects primitive data types
			isAdmin,
			isOrgAdmin,
			hasActiveJpdLicense,
		};
	}, [
		loomTouchpointVariant,
		hasConfluenceAccess,
		issueType,
		issueStatusDetails,
		isAdmin,
		isOrgAdmin,
		hasActiveJpdLicense,
	]);

	return (
		<CrossFlowPostOfficeContext.Provider value={context}>
			{children}
		</CrossFlowPostOfficeContext.Provider>
	);
};
